import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Info,
  AttachMoney,
  Assessment,
  Timeline,
  AccountBalance
} from '@mui/icons-material';
import { 
  getCashflowData, 
  generateCashflowStatement
} from '../../../services/request/Cashflow';
import {
  CashflowSummary,
  FinancialHealthScore,
  CashflowForecast,
  CashflowStatement
} from '../../../models/CashflowInterface';

const CashflowDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<CashflowSummary | null>(null);
  const [healthScore, setHealthScore] = useState<FinancialHealthScore | null>(null);
  const [forecast, setForecast] = useState<CashflowForecast | null>(null);
  const [statement, setStatement] = useState<CashflowStatement | null>(null);
  
  // Date range state
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [periodType, setPeriodType] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY');

  useEffect(() => {
    loadCashflowData();
  }, []);

  const loadCashflowData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getCashflowData(startDate, endDate);
      setSummary(data.summary);
      setHealthScore(data.healthScore);
      setForecast(data.forecast);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cashflow data');
    } finally {
      setLoading(false);
    }
  };

  const generateStatement = async () => {
    try {
      setLoading(true);
      const response = await generateCashflowStatement({
        startDate,
        endDate,
        periodType
      });
      setStatement(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate statement');
    } finally {
      setLoading(false);
    }
  };

  const getHealthScoreColor = (rating: string) => {
    switch (rating) {
      case 'EXCELLENT': return 'success';
      case 'GOOD': return 'info';
      case 'FAIR': return 'warning';
      case 'POOR': return 'error';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getRiskLevelColor = (riskLevel: string) => {
    if (riskLevel === 'LOW') return 'success';
    if (riskLevel === 'MEDIUM') return 'warning';
    return 'error';
  };

  const getReliabilityColor = (reliability: string) => {
    if (reliability === 'HIGH') return 'success';
    if (reliability === 'MEDIUM') return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadCashflowData} sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Cashflow Dashboard
      </Typography>

      {/* Date Range Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Period Type</InputLabel>
              <Select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value as any)}
              >
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="QUARTERLY">Quarterly</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              onClick={loadCashflowData}
              fullWidth
              startIcon={<Assessment />}
            >
              Refresh Data
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Financial Health Score */}
      {healthScore && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Financial Health Score
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Typography variant="h2" color="primary">
                    {healthScore.overallScore}
                  </Typography>
                  <Chip
                    label={healthScore.healthStatus}
                    color={getHealthScoreColor(healthScore.healthStatus) as any}
                    size="medium"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Profitability Score
                    </Typography>
                    <Typography variant="h6">
                      {healthScore.profitabilityScore.toFixed(1)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Liquidity Score
                    </Typography>
                    <Typography variant="h6">
                      {healthScore.liquidityScore.toFixed(1)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Asset Quality Score
                    </Typography>
                    <Typography variant="h6">
                      {healthScore.assetQualityScore.toFixed(1)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Efficiency Score
                    </Typography>
                    <Typography variant="h6">
                      {healthScore.efficiencyScore.toFixed(1)}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Total Cash Inflow
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(summary.cashFlowAnalysis.totalCashInflow)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TrendingDown color="error" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Total Cash Outflow
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(summary.cashFlowAnalysis.totalCashOutflow)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <AccountBalance color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Net Cash Flow
                    </Typography>
                    <Typography variant="h6" color={summary.cashFlowAnalysis.netCashFlow >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(summary.cashFlowAnalysis.netCashFlow)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircle color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Current Cash Position
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(summary.currentCashPosition)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Key Metrics */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Key Metrics
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Cash Flow Growth Rate"
                      secondary={`${summary.cashFlowAnalysis.cashFlowGrowthRate.toFixed(2)}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assessment color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Operating Margin"
                      secondary={`${summary.cashFlowAnalysis.operatingMargin.toFixed(2)}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <AccountBalance color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Liquidity Ratio"
                      secondary={summary.cashFlowAnalysis.liquidityRatio.toFixed(2)}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Alerts & Recommendations
                </Typography>
                {summary.cashFlowAnalysis.riskAlerts && summary.cashFlowAnalysis.riskAlerts.length > 0 ? (
                  <List>
                    {summary.cashFlowAnalysis.riskAlerts.map((alert) => (
                      <ListItem key={`alert-${alert}`}>
                        <ListItemIcon>
                          <Warning color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={alert}
                          secondary="Risk Alert"
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary">
                    No risk alerts at this time
                  </Typography>
                )}
                {summary.cashFlowAnalysis.recommendations && summary.cashFlowAnalysis.recommendations.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Recommendations:
                    </Typography>
                    <List dense>
                      {summary.cashFlowAnalysis.recommendations.map((rec) => (
                        <ListItem key={`rec-${rec}`}>
                          <ListItemIcon>
                            <Info color="info" />
                          </ListItemIcon>
                          <ListItemText
                            primary={rec}
                            secondary="Recommendation"
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Forecast */}
      {forecast && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {forecast.forecastMonths}-Month Cash Flow Forecast
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Total Projected Net Cash Flow: {formatCurrency(forecast.projectedNetCashFlow)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Risk Level:
              </Typography>
              <Chip 
                label={forecast.riskLevel} 
                color={getRiskLevelColor(forecast.riskLevel) as any}
                size="small" 
              />
              <Typography variant="body2" color="textSecondary">
                Reliability:
              </Typography>
              <Chip 
                label={forecast.forecastReliability} 
                color={getReliabilityColor(forecast.forecastReliability) as any}
                size="small" 
              />
            </Box>
            <Grid container spacing={2}>
              {forecast.monthlyProjections.slice(0, 6).map((month, index) => (
                <Grid item xs={12} sm={6} md={4} key={`forecast-${month.month}-${index}`}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">{new Date(month.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</Typography>
                    <Typography variant="h6" color={month.projectedNetFlow >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(month.projectedNetFlow)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Inflow: {formatCurrency(month.projectedInflow)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Outflow: {formatCurrency(month.projectedOutflow)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={month.confidenceLevel * 100}
                      sx={{ mt: 1 }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Confidence: {(month.confidenceLevel * 100).toFixed(0)}%
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Generate Statement Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          onClick={generateStatement}
          startIcon={<Timeline />}
          disabled={loading}
        >
          Generate Cash Flow Statement
        </Button>
      </Box>

      {/* Generated Statement */}
      {statement && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cash Flow Statement ({statement.period})
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Period: {statement.startDate} to {statement.endDate}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Operating Activities
                </Typography>
                <Typography variant="body2">
                  Net Income: {formatCurrency(statement.operatingActivities.netIncome)}
                </Typography>
                <Typography variant="body2">
                  Depreciation: {formatCurrency(statement.operatingActivities.depreciation)}
                </Typography>
                <Typography variant="body2">
                  Net Operating Cash Flow: {formatCurrency(statement.operatingActivities.netOperatingCashFlow)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Investing Activities
                </Typography>
                <Typography variant="body2">
                  Equipment Purchases: {formatCurrency(statement.investingActivities.equipmentPurchases)}
                </Typography>
                <Typography variant="body2">
                  Property Purchases: {formatCurrency(statement.investingActivities.propertyPurchases)}
                </Typography>
                <Typography variant="body2">
                  Net Investing Cash Flow: {formatCurrency(statement.investingActivities.netInvestingCashFlow)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" gutterBottom>
                  Financing Activities
                </Typography>
                <Typography variant="body2">
                  Loan Proceeds: {formatCurrency(statement.financingActivities.loanProceeds)}
                </Typography>
                <Typography variant="body2">
                  Loan Payments: {formatCurrency(statement.financingActivities.loanPayments)}
                </Typography>
                <Typography variant="body2">
                  Net Financing Cash Flow: {formatCurrency(statement.financingActivities.netFinancingCashFlow)}
                </Typography>
              </Grid>
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Box textAlign="center">
              <Typography variant="h5" color={statement.netCashFlow >= 0 ? 'success.main' : 'error.main'}>
                Net Cash Flow: {formatCurrency(statement.netCashFlow)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Beginning Cash: {formatCurrency(statement.beginningCash)} | 
                Ending Cash: {formatCurrency(statement.endingCash)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CashflowDashboard;
